class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingBlock = this.querySelector('#trending-keywords');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;

    this.setupEventListeners();
    this.showTrending();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  // ✅ SHOW TRENDING
  showTrending() {
    if (this.trendingBlock) this.trendingBlock.classList.remove('hidden');
    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.add('hidden');

    this.open();
  }

  // ✅ SHOW RESULTS
  showResults() {
    if (this.trendingBlock) this.trendingBlock.classList.add('hidden');
    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.remove('hidden');
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    // ✅ EMPTY → TRENDING
    if (!this.searchTerm.length) {
      this.showTrending();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showTrending();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showTrending();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  // ✅ FIXED (NO ERROR)
  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (!el || !previousTerm) return;

    const text = el.innerText || '';
    const matches = text.match(new RegExp(previousTerm, 'g')) || [];

    if (matches.length > 1) return;

    el.innerText = text.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');

    const items = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);

    if (!items.length) return;

    let index = items.indexOf(selected);

    if (moveUp) {
      index = index <= 0 ? items.length - 1 : index - 1;
    } else {
      index = index === items.length - 1 ? 0 : index + 1;
    }

    items.forEach(el => el.setAttribute('aria-selected', false));
    items[index].setAttribute('aria-selected', true);

    this.input.setAttribute('aria-activedescendant', items[index].id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a');
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(text => {
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

        this.allPredictiveSearchInstances.forEach(i => {
          i.cachedResults[queryKey] = markup;
        });

        this.renderSearchResults(markup);
      })
      .catch(() => this.close());
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;

    this.setAttribute('results', true);
    this.showResults();
    this.open();
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);